import { z } from "zod";

/**
 * File upload metadata schema
 */
export const FileUploadMetadataSchema = z.object({
  jobId: z.string().min(1, "Job ID is required"),
  storageId: z.string().min(1, "Storage ID is required"),
  fileName: z
    .string()
    .min(1, "File name is required")
    .max(255, "File name too long"),
  fileSize: z
    .number()
    .int("File size must be an integer")
    .positive("File size must be positive")
    .max(10 * 1024 * 1024, "File size exceeds 10MB limit"),
  mimeType: z.enum(
    [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/bmp",
      "image/heic",
      "image/heif",
    ],
    {
      message: "Unsupported file type",
    }
  ),
  convertHeicAndHeif: z.boolean(),
});

export type FileUploadMetadata = z.infer<typeof FileUploadMetadataSchema>;

/**
 * Job creation schema
 */
export const JobCreationSchema = z.object({
  email: z.string().email("Invalid email format"),
  fileCount: z
    .number()
    .int("File count must be an integer")
    .positive("File count must be positive")
    .max(100000, "File count exceeds maximum"),
  totalSizeBytes: z
    .number()
    .int("Total size must be an integer")
    .nonnegative("Total size cannot be negative")
    .max(2 * 1024 * 1024 * 1024, "Total size exceeds 2GB limit"),
  categoryMode: z.enum(
    ["predefined", "custom", "color", "semantic", "hybrid"],
    {
      message: "Invalid category mode",
    }
  ),
  customCategories: z
    .object({
      categories: z
        .array(
          z
            .string()
            .min(2, "Category name too short")
            .max(50, "Category name too long")
            .regex(
              /^[a-zA-Z0-9\s\-_]+$/,
              "Category name contains invalid characters"
            )
        )
        .min(1, "At least one category required")
        .max(20, "Maximum 20 categories allowed"),
      classificationMode: z.enum(["color", "content", "semantic", "mixed"]),
      instructions: z.string().max(500, "Instructions too long").optional(),
      fallbackToPredefined: z.boolean().optional(),
    })
    .optional(),
  convertHeicAndHeif: z.boolean(),
});

export type JobCreationInput = z.infer<typeof JobCreationSchema>;

